import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { auth, firestore } from '../config/firebase';
import { Income, IncomeFrequency } from '../types/income';
import { Alert, Platform } from 'react-native';
import AppLoadingScreen from '../components/AppLoadingScreen';
// Modular Firestore API for web
import {
  doc as docModular,
  onSnapshot as onSnapshotModular,
  updateDoc as updateDocModular
} from 'firebase/firestore';

interface IncomeContextType {
  income: Income;
  updateIncome: (income: Partial<Income>) => Promise<void>;
}

const defaultIncome: Income = {
  amount: 0,
  frequency: 'monthly',
  monthlyIncome: 0,
  unallocatedAmount: 0,
};

const IncomeContext = createContext<IncomeContextType & { isInitialized: boolean } | undefined>(undefined);

export const useIncome = () => {
  const context = useContext(IncomeContext);
  if (!context) {
    throw new Error('useIncome must be used within an IncomeProvider');
  }
  return context;
};

interface IncomeProviderProps {
  children: ReactNode;
}

export const IncomeProvider = ({ children }: IncomeProviderProps) => {
  const [income, setIncome] = useState<Income>(defaultIncome);
  const [isInitialized, setIsInitialized] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  // Helper: true if using modular web Firestore
  const isModularFirestore = Platform.OS === 'web';

  // Listen for auth state changes and set userId
  useEffect(() => {
    const authInstance = typeof auth === 'function' ? auth() : auth;
    const unsubscribe = authInstance.onAuthStateChanged((user: any) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
        setIncome(defaultIncome);
        setIsInitialized(true);
      }
    });
    return () => unsubscribe();
  }, []);

  // Subscribe to Firestore income updates when userId is available
  useEffect(() => {
    if (!userId) return;
    setIsInitialized(false);
    const firestoreInstance = typeof firestore === 'function' ? firestore() : firestore;
    let unsubscribe: any;
    if (isModularFirestore) {
      const userRef = docModular(firestoreInstance, 'users', userId);
      unsubscribe = onSnapshotModular(userRef, (docSnap: any) => {
        let data: any = undefined;
        try {
          data = docSnap.data();
        } catch {}
        if (docSnap.exists() && data && data.income) {
          setIncome(data.income);
        } else {
          setIncome(defaultIncome);
        }
        setIsInitialized(true);
      });
    } else {
      const userRef = firestoreInstance.collection('users').doc(userId);
      unsubscribe = userRef.onSnapshot(
        (docSnap: any) => {
          let data: any = undefined;
          try {
            data = typeof docSnap.data === 'function' ? docSnap.data() : docSnap.data;
          } catch (e) {
            data = undefined;
          }
          if (docSnap.exists && data && data.income) {
            setIncome(data.income);
          } else {
            setIncome(defaultIncome);
          }
          setIsInitialized(true);
        },
        (error: any) => {
          // Handle authentication errors gracefully
          if (error.code === 'permission-denied' || error.message.includes('permission')) {
            console.log('IncomeContext: Permission denied for income sync (likely during logout), ignoring error');
            // Don't show alert for permission issues during logout
            setIsInitialized(true);
            return;
          }
          console.error('IncomeContext: Income sync error:', error);
          Alert.alert('Error', 'Failed to sync income data');
          setIsInitialized(true);
        }
      );
    }
    return () => unsubscribe();
  }, [userId]);

  const updateIncome = async (newIncomeData: Partial<Income>) => {
    try {
      const authInstance = typeof auth === 'function' ? auth() : auth;
      const firestoreInstance = typeof firestore === 'function' ? firestore() : firestore;
      const user = authInstance.currentUser;
      if (!user) {
        console.warn('IncomeContext: updateIncome called but no authenticated user. Skipping update.');
        return;
      }
      // Calculate monthly income if amount or frequency changes
      let updatedIncome = { ...income, ...newIncomeData };
      if (newIncomeData.amount !== undefined || newIncomeData.frequency !== undefined) {
        const amount = newIncomeData.amount ?? income.amount;
        const frequency = newIncomeData.frequency ?? income.frequency;
        switch (frequency) {
          case 'bi-weekly':
            updatedIncome.monthlyIncome = Math.round((amount * 26) / 12);
            break;
          case 'weekly':
            updatedIncome.monthlyIncome = Math.round((amount * 52) / 12);
            break;
          case 'daily':
            updatedIncome.monthlyIncome = Math.round(amount * 30);
            break;
          default:
            updatedIncome.monthlyIncome = amount;
        }
      }
      if (isModularFirestore) {
        // Modular API (web)
        const userDocRef = docModular(firestoreInstance, 'users', user.uid);
        await updateDocModular(userDocRef, {
          income: updatedIncome,
          lastUpdated: new Date().toISOString()
        });
      } else {
        // Classic API (native)
        const userDocRef = firestoreInstance.collection('users').doc(user.uid);
        await userDocRef.update({
          income: updatedIncome,
          lastUpdated: new Date().toISOString()
        });
      }
      setIncome(updatedIncome);
    } catch (error) {
      console.error('IncomeContext: Failed to update income:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to update income');
    }
  };

  if (!isInitialized) {
    console.log('IncomeProvider: Not initialized yet, returning loading spinner.');
    return <AppLoadingScreen showBrand={false} />;
  }

  console.log('IncomeProvider: Rendering provider with income:', income);
  return (
    <IncomeContext.Provider value={{ income, updateIncome, isInitialized }}>
      {children}
    </IncomeContext.Provider>
  );
}; 